/**
 * ProjectFilesTab — the documents every agent in the project can read.
 *
 * These go through the platform's ordinary data-bundle pipeline, so the file
 * types, size limits, chunking and retrieval behaviour are exactly the ones
 * users already know from agent knowledge files. Nothing here is a parallel
 * file system.
 *
 * Managers add and remove; everyone sees the list, because knowing what the
 * agent has read is part of trusting its answers.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslator } from '../hooks/useTranslator';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import { FilePreviewModal } from '@/components/common';
import projectService from '../services/projectService';
import { useFeature } from '@/hooks/useFeature';
import { LibraryLinkCta } from './LibraryLinkCta';
import { LibraryLinkModal } from './LibraryLinkModal';
import type { ProjectFile, ProjectLibraryLink, ProjectSummary } from '../types';
import { SkeletonRows, EmptyBlock, ErrorBlock } from './StateBlocks';
import './ProjectTabs.css';

interface ProjectFilesTabProps {
  project: ProjectSummary;
}

/** The API reports size in MB (`file_size_mb`), not bytes. */
function formatSize(sizeMb?: number | null): string {
  if (!sizeMb || sizeMb < 0) return '';
  if (sizeMb < 0.1) return `${Math.max(1, Math.round(sizeMb * 1024))} KB`;
  return `${sizeMb.toFixed(1)} MB`;
}

/** What the file is actually called. `file_display_name` is what the uploader
 *  named it; `file_name` is the stored name. Never "Untitled". */
function displayName(file: ProjectFile): string {
  return (file.file_display_name || file.file_name || '').trim();
}

export function ProjectFilesTab({ project }: ProjectFilesTabProps) {
  const { t, tRef } = useTranslator();
  const [files, setFiles] = useState<ProjectFile[]>([]);
  const [bundleMissing, setBundleMissing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ProjectFile | null>(null);
  const [deleting, setDeleting] = useState(false);
  /** Reuses the app's existing preview modal — same viewer, same download
   *  button, same supported types as everywhere else files are shown. */
  const [previewing, setPreviewing] = useState<ProjectFile | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Library links — folders and files shared into the project from the Vault.
  // Only meaningful when the tenant's Library module is on; with it off the
  // endpoints refuse. Linking more needs a Library of one's own as well
  // (LibraryLinkCta); the links already made are listed regardless of role.
  const libraryOn = useFeature('modules.library');
  const [links, setLinks] = useState<ProjectLibraryLink[]>([]);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [pendingUnlink, setPendingUnlink] = useState<ProjectLibraryLink | null>(null);
  const [unlinking, setUnlinking] = useState(false);

  const canManage = project.can_manage && !project.is_archived;

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      projectService.listFiles(project.project_id),
      // Library off → no links to fetch, and the endpoint would refuse.
      libraryOn
        ? projectService.listLibraryLinks(project.project_id).catch(() => [])
        : Promise.resolve([]),
    ])
      .then(([response, linkRows]) => {
        setFiles(response.files);
        setBundleMissing(response.bundle_missing);
        setLinks(linkRows);
      })
      .catch(() => setError(
        tRef.current('projects.files.loadFailed') || t('projects.files.loadFailed'),
      ))
      .finally(() => setLoading(false));
    // `t` out of the deps on purpose — see useTranslator.ts.
  }, [project.project_id, libraryOn, tRef]);

  useEffect(() => { load(); }, [load]);

  const uploadMany = async (list: FileList | File[]) => {
    const chosen = Array.from(list);
    if (!chosen.length) return;
    setUploading(true);
    setUploadError(null);
    try {
      // Sequential, not parallel: each upload is chunked and embedded
      // server-side, and firing five at once is how a user discovers the size
      // limit as five simultaneous failures.
      for (const file of chosen) {
        await projectService.uploadFile(project.project_id, file);
      }
      load();
    } catch (err: any) {
      setUploadError(err?.response?.data?.error
        || (t('projects.files.uploadFailed') || 'That file could not be added.'));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await projectService.removeFile(project.project_id, pendingDelete.file_id);
      setFiles(prev => prev.filter(f => f.file_id !== pendingDelete.file_id));
      setPendingDelete(null);
    } catch {
      setUploadError(t('projects.files.deleteFailed') || 'Could not remove that file.');
    } finally {
      setDeleting(false);
    }
  };

  const handleUnlink = async () => {
    if (!pendingUnlink) return;
    setUnlinking(true);
    try {
      await projectService.removeLibraryLink(project.project_id, pendingUnlink.link_id);
      setLinks(prev => prev.filter(l => l.link_id !== pendingUnlink.link_id));
      setPendingUnlink(null);
    } catch {
      setUploadError(t('projects.library.unlinkFailed') || 'Could not unlink that item.');
    } finally {
      setUnlinking(false);
    }
  };

  if (loading) return <div className="pj-tab"><SkeletonRows rows={3} /></div>;
  if (error) return <div className="pj-tab"><ErrorBlock message={error} onRetry={load} /></div>;

  if (bundleMissing) {
    return (
      <div className="pj-tab">
        <ErrorBlock
          message={t('projects.files.noStore')
            || 'This project has no file store. Reload the page, and contact an administrator if it persists.'}
          onRetry={load}
        />
      </div>
    );
  }

  return (
    <div className="pj-tab files-tab">
      {/* The toolbar is FROZEN; only the file list below scrolls. A drop zone
          that scrolls away is a drop zone nobody can drop on. */}
      {canManage && (
      <div dir="auto" className="files-toolbar">
        <div
          className={`files-drop ${dragging ? 'is-dragging' : ''} ${uploading ? 'is-busy' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => {
            e.preventDefault();
            setDragging(false);
            if (!uploading) uploadMany(e.dataTransfer.files);
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 9 12 4 17 9" />
            <line x1="12" y1="4" x2="12" y2="16" />
          </svg>
          <p className="files-drop-title">
            {uploading
              ? (t('projects.files.uploading') || 'Adding files…')
              : (t('projects.files.dropTitle') || 'Drop files here')}
          </p>
          <p dir="auto" className="files-drop-hint">
            {t('projects.files.dropHint')
              || 'Every agent in this project can read them, in every conversation.'}
          </p>
          <button
            type="button"
            className="pj-btn pj-btn-secondary pj-btn-sm"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            {t('projects.files.browse') || 'Choose files'}
          </button>
          <input
            ref={inputRef}
            type="file"
            multiple
            className="files-input"
            onChange={e => e.target.files && uploadMany(e.target.files)}
            aria-label={t('projects.files.browse') || 'Choose files'}
          />
        </div>

        <LibraryLinkCta onOpen={() => setLinkModalOpen(true)} />
      </div>
      )}

      {uploadError && <div className="pj-form-error">{uploadError}</div>}

      <div className="files-scroll">
      {links.length > 0 && (
        <section className="files-links">
          <h3 className="files-links-title">
            {t('projects.library.sectionTitle') || 'From the Library'}
          </h3>
          <ul className="files-list">
            {links.map(link => (
              <li key={link.link_id} className="files-row">
                <span className="files-row-icon" aria-hidden="true">
                  {link.folder_id ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
                      <path d="M14 3v5h5" />
                    </svg>
                  )}
                </span>
                <span className="files-row-text">
                  <span dir="auto" className="files-row-name">
                    {(link.display_name || link.node_name || '').trim()
                      || (t('projects.files.unnamed') || 'Untitled')}
                  </span>
                  {/* The Library's own description, carried across untouched —
                      the same sentences the project's agents receive. */}
                  {link.summary && (
                    <span className="files-row-summary bidi-clamp" dir="auto">{link.summary}</span>
                  )}
                </span>
                {link.include_as_context && (
                  <span className="pj-pill pj-pill-neutral">
                    {t('projects.library.contextPill') || 'In context'}
                  </span>
                )}
                {canManage && (
                  <button
                    type="button"
                    className="pj-icon-btn"
                    onClick={() => setPendingUnlink(link)}
                    aria-label={t('projects.library.unlink') || 'Unlink'}
                    title={t('projects.library.unlink') || 'Unlink'}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18.84 12.25l1.72-1.71a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M5.17 11.75l-1.72 1.71a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      <line x1="2" y1="2" x2="22" y2="22" />
                    </svg>
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* For a manager the drop zone above IS the empty state — stacking a
          second "No files yet" panel under it says the same thing twice and
          leaves the tab looking half-finished. Members, who have no drop zone,
          still get the explanation. */}
      {files.length === 0 && !canManage ? (
        <EmptyBlock
          icon={(
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
              <path d="M14 3v5h5" />
            </svg>
          )}
          title={t('projects.files.emptyTitle') || 'No files yet'}
          body={t('projects.files.emptyBodyMember')
            || 'A project manager has not added any files yet.'}
        />
      ) : files.length > 0 && (
        <ul className="files-list">
          {files.map(file => (
            <li key={file.bundle_file_id || file.file_id} className="files-row">
              <button
                type="button"
                className="files-row-open"
                onClick={() => setPreviewing(file)}
                title={t('projects.files.open') || 'Open'}
              >
              <span className="files-row-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
                  <path d="M14 3v5h5" />
                </svg>
              </span>
              <span className="files-row-text">
                <span dir="auto" className="files-row-name">
                  {displayName(file) || (t('projects.files.unnamed') || 'Untitled file')}
                </span>
                {(file as ProjectFile & { summary?: string | null }).summary && (
                  <span dir="auto" className="files-row-summary bidi-clamp">
                    {String((file as ProjectFile & { summary?: string | null }).summary)}
                  </span>
                )}
                <span className="files-row-meta">
                  {/* A small file rounds to 0 MB, so fall back to the type
                      rather than leaving the line blank. */}
                  {formatSize(file.file_size_mb)
                    || (file.file_type || '').toUpperCase()}
                  {file.source_type === 'vault' && (
                    <> · {t('projects.files.fromLibrary') || 'from the Library'}</>
                  )}
                </span>
              </span>
              </button>
              {file.processing_status && file.processing_status !== 'completed' && (
                <span className="pj-pill pj-pill-pending">
                  {t('projects.files.processing') || 'Processing'}
                </span>
              )}
              <a
                className="pj-icon-btn"
                href={`/api/v1/files/download-by-id/${file.file_id}`}
                download
                onClick={e => e.stopPropagation()}
                aria-label={t('projects.files.download') || 'Download'}
                title={t('projects.files.download') || 'Download'}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              </a>
              {canManage && (
                <button
                  type="button"
                  className="pj-icon-btn"
                  onClick={() => setPendingDelete(file)}
                  aria-label={t('projects.files.remove') || 'Remove file'}
                  title={t('projects.files.remove') || 'Remove file'}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
                  </svg>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      </div>

      <ConfirmModal
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDelete}
        title={t('projects.files.removeTitle') || 'Remove this file?'}
        message={(t('projects.files.removeBody')
          || 'The agents in this project will no longer be able to read “{{name}}”.')
          .replace('{{name}}', pendingDelete ? displayName(pendingDelete) : '')}
        confirmText={t('projects.files.remove') || 'Remove'}
        cancelText={t('common.cancel') || 'Cancel'}
        variant="danger"
        loading={deleting}
      />

      <ConfirmModal
        isOpen={!!pendingUnlink}
        onClose={() => setPendingUnlink(null)}
        onConfirm={handleUnlink}
        title={t('projects.library.unlinkTitle') || 'Unlink from this project?'}
        message={(t('projects.library.unlinkBody')
          || 'The agents here will no longer read “{{name}}”. Nothing is deleted from the Library.')
          .replace('{{name}}', (pendingUnlink?.display_name || pendingUnlink?.node_name || '') as string)}
        confirmText={t('projects.library.unlink') || 'Unlink'}
        cancelText={t('common.cancel') || 'Cancel'}
        variant="danger"
        loading={unlinking}
      />

      <LibraryLinkModal
        isOpen={linkModalOpen}
        projectId={project.project_id}
        linkedFolderIds={new Set(links.map(l => l.folder_id).filter(Boolean) as string[])}
        linkedFileIds={new Set(links.map(l => l.file_id).filter(Boolean) as string[])}
        onClose={() => setLinkModalOpen(false)}
        onLinked={load}
      />

      {previewing && (
        <FilePreviewModal
          visible
          fileId={previewing.file_id}
          filePath={previewing.file_path || undefined}
          fileName={displayName(previewing)}
          fileType={previewing.file_type || undefined}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  );
}

export default ProjectFilesTab;
